import { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "@/lib/config";
import { useInView, useReducedMotion } from "@/lib/motion";
import { LeafScene, LessonChrome, PhoneFrame } from "./Phone";
import { DayHeading, DaySection, Slab } from "./day";

/**
 * A real lesson beat from the scene renderer (packages/scene): the backend's player page (GET /scene/player)
 * in an iframe, fed a compiled example scene (GET /scene/examples/:name) over postMessage like the app's WebView:
 * {type:'stage'}, {type:'scene', scene, beat}, then {type:'beat', beat} on Continue. Falls back to a drawn scene
 * if the server is down.
 */
type Beat = { narration: string };

const SCENES = [
  { id: "photosynthesis", label: "Photosynthesis" },
  { id: "pond-water", label: "Pond water" },
  { id: "sourdough", label: "Sourdough" },
] as const;

/** Narration of the photosynthesis scene, for the drawn fallback. */
const FALLBACK: Beat[] = [
  { narration: "Every leaf on Earth is quietly running a factory, and its power source is the Sun." },
  { narration: "It pulls in carbon dioxide from the air through tiny pores in its leaves." },
  { narration: "Sunlight splits water, and the plant builds sugar, six carbon atoms at a time." },
  { narration: "Zoom far enough in and every leaf cell is packed with green machines." },
];

const textOf = (n: unknown) => (typeof n === "string" ? n : ((n as { text?: string })?.text ?? ""));

function ScenePlayer() {
  const [scene, setScene] = useState<(typeof SCENES)[number]["id"]>(SCENES[0].id);
  const [data, setData] = useState<{ scene: object; beats: Beat[] } | null>(null);
  const [mode, setMode] = useState<"loading" | "live" | "fallback">("loading");
  const [at, setAt] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const reduced = useReducedMotion();
  const [box, near] = useInView<HTMLDivElement>("300px");

  const send = useCallback((m: object) => frame.current?.contentWindow?.postMessage(JSON.stringify(m), "*"), []);

  // Load the scene once the section is near; no scene server -> the drawn fallback.
  useEffect(() => {
    if (!near) return;
    let stop = false;
    setMode("loading");
    setData(null);
    setAt(0);
    fetch(`${API_URL}/scene/examples/${scene}`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then((m: { scene: { beats: { narration: unknown }[] } }) => {
        if (stop) return;
        if (!m.scene?.beats?.length) throw new Error();
        setData({ scene: m.scene, beats: m.scene.beats.map(b => ({ narration: textOf(b.narration) })) });
      })
      .catch(() => {
        if (!stop) setMode("fallback");
      });
    return () => {
      stop = true;
    };
  }, [near, scene]);

  // The page says 'ready' once CanvasKit runs: frame the picture above the narration band, then hand it the scene.
  const dataRef = useRef(data);
  dataRef.current = data;
  useEffect(() => {
    const on = (e: MessageEvent) => {
      if (!frame.current || e.source !== frame.current.contentWindow) return;
      let m: { type?: string } | null = null;
      try {
        m = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }
      if (m?.type === "ready" && dataRef.current) {
        const f = frame.current;
        send({ type: "stage", stage: { x: 0, y: 0, w: f.clientWidth, h: f.clientHeight, r: 0, band: 0.4 } });
        send({ type: "still", on: reduced });
        send({ type: "scene", scene: dataRef.current.scene, beat: 0 });
        setMode("live");
      } else if (m?.type === "error") setMode("fallback");
    };
    addEventListener("message", on);
    return () => removeEventListener("message", on);
  }, [send, reduced]);

  // If the page never says ready, fall back rather than show a blank screen.
  useEffect(() => {
    if (mode !== "loading" || !data) return;
    const t = setTimeout(() => setMode(m => (m === "loading" ? "fallback" : m)), 12000);
    return () => clearTimeout(t);
  }, [mode, data]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const list = mode === "fallback" || !data ? FALLBACK : data.beats;
  const last = at >= list.length - 1;
  const next = (instant = false) => {
    const to = last ? 0 : at + 1;
    setAt(to);
    if (mode === "live") send({ type: "beat", beat: to, fast: instant });
  };

  return (
    <div ref={box} className="flex flex-col items-center gap-5">
      <div role="tablist" aria-label="Pick a scene" className="flex flex-wrap justify-center gap-2">
        {SCENES.map(s => (
          <button
            key={s.id}
            role="tab"
            aria-selected={scene === s.id}
            type="button"
            onClick={() => setScene(s.id)}
            className={`rounded-xl border-2 border-b-[4px] px-3.5 py-1.5 text-[13px] font-black transition active:translate-y-0.5 active:border-b-2 ${scene === s.id ? "border-[#4128B8] bg-[#6B4EFF] text-white" : "border-[#E7E3F0] bg-white text-[#1C1A27] hover:bg-[#FAF9FF]"}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <PhoneFrame>
        {mode !== "fallback" && near && data && (
          <iframe
            key={scene}
            ref={frame}
            src={`${API_URL}/scene/player`}
            title={`Animated lesson scene: ${SCENES.find(s => s.id === scene)?.label}`}
            className="pointer-events-none absolute inset-0 h-full w-full border-0"
            style={{ opacity: mode === "live" ? 1 : 0, transition: "opacity 500ms" }}
            loading="lazy"
          />
        )}
        {mode === "fallback" && <LeafScene beat={at} />}
        {mode === "loading" && (
          <div className="absolute inset-0 grid place-items-center">
            <span className="size-2 rounded-full bg-white/60 la-blink" aria-label="Loading the scene" />
          </div>
        )}
        <LessonChrome
          beats={list.length}
          at={at}
          narration={list[at]?.narration ?? ""}
          onKnow={() => {
            setToast("Noted. Later lessons will go lighter on this.");
            next(true);
          }}
          onAsk={() => document.getElementById("tutor")?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" })}
          onContinue={() => next()}
          continueLabel={last ? "Replay" : "Continue"}
        >
          {toast && (
            <div className="la-fade-up absolute inset-x-4 top-[60px] z-30 rounded-xl border border-white/15 bg-[rgba(24,16,60,0.94)] px-3 py-2 text-[12.5px] font-extrabold text-white">
              <span className="text-gold">✓ </span>
              {toast}
            </div>
          )}
        </LessonChrome>
      </PhoneFrame>
      <p className="max-w-[300px] text-center text-xs leading-snug font-bold text-[#6B6780]">
        {mode === "fallback" ? "A drawn stand-in: the scene server isn’t reachable right now." : "A real lesson. Tap Continue to move it forward."}
      </p>
    </div>
  );
}

export function LessonBeat() {
  return (
    <DaySection id="lesson" label="A lesson beat" tone="tint">
      <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
        <div>
          <DayHeading
            eyebrow="Lessons"
            title="Lessons you’ll actually finish."
            intro="A few minutes each, one idea at a time, told with an animated scene instead of a wall of text. You set the pace, so nothing moves on before you’ve got it."
          />
          <Slab className="mt-8 p-5 sm:p-6">
            <div className="grid h-12 place-items-center rounded-2xl border-b-[5px] border-[#4128B8] bg-[#6B4EFF] text-sm font-black tracking-[0.06em] text-white">CONTINUE</div>
            <p className="mt-3 text-[15px] leading-relaxed text-[#6B6780]">Go at your own pace. The next idea is ready the moment you are.</p>
            <div className="mt-5 grid gap-4 border-t-2 border-[#F0EDF7] pt-5 sm:grid-cols-2">
              <div>
                <p className="text-[15px] font-extrabold text-[#6B6780]">I know this</p>
                <p className="mt-1 text-[14px] leading-relaxed text-[#6B6780]">Skip what you already know. Later lessons adjust.</p>
              </div>
              <div>
                <p className="text-[15px] font-extrabold text-[#6B6780]">Ask a question</p>
                <p className="mt-1 text-[14px] leading-relaxed text-[#6B6780]">Stuck? Ask about what’s on screen without losing your place.</p>
              </div>
            </div>
          </Slab>
        </div>
        <ScenePlayer />
      </div>
    </DaySection>
  );
}
